import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type Job, type JobStatus, type Stage } from "../lib/api";
import { displaySkill, formatDay, formatFee, formatSalary, JOB_STATUS_LABEL } from "../lib/format";
import { JobForm } from "./JobForm";
import { JobRoleTerms } from "./JobRoleTerms";
import { JobShortlists } from "./JobShortlists";
import { JobSourcing } from "./JobSourcing";
import { PipelineBoard } from "./PipelineBoard";
import { useTitle } from "../lib/title";

const pad = (n: number) => String(n).padStart(2, "0");

export function JobPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  useTitle(job?.title ?? "Job");
  const [stages, setStages] = useState<Stage[]>([]);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [cardCount, setCardCount] = useState<number | null>(null);
  // Bumped when someone joins or leaves the pipeline, so the suggestions refresh; boardKey remounts the board.
  const [pipelineChanged, setPipelineChanged] = useState(0);
  const [boardKey, setBoardKey] = useState(0);
  const lastCount = useRef<number | null>(null);
  const onCount = useCallback((n: number) => {
    if (lastCount.current !== null && lastCount.current !== n) setPipelineChanged((c) => c + 1);
    lastCount.current = n;
    setCardCount(n);
  }, []);
  const load = useAction();
  const act = useAction();

  const reload = useCallback(
    () =>
      load.run(async () => {
        const r = await api.job(id);
        setJob(r.job);
        setStages(r.stages);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );
  useEffect(() => void reload(), [reload]);

  if (!job) {
    return (
      <>
        <p className="label">
          <Link to="/jobs">← Jobs</Link>
        </p>
        <ErrorNotice error={load.error} />
        {!load.error && <p className="label">Loading…</p>}
      </>
    );
  }

  const setStatus = (status: JobStatus) =>
    void act.run(async () => {
      setJob((await api.updateJob(job.id, { status })).job);
    });

  const facts = [job.location, formatSalary(job.salaryMin, job.salaryMax, job.currency)].filter(Boolean);

  return (
    <>
      <div className="page-head">
        <p className="label">
          <Link to="/jobs">← Jobs</Link> · <Link to={`/companies/${job.company.id}`}>{job.company.name}</Link>
        </p>
        <h1 className="display">{job.title}</h1>
        {facts.length > 0 && <p className="lede">{facts.join(" · ")}</p>}
      </div>

      <section className="section" aria-labelledby="status-h">
        <div className="section-head">
          <h2 id="status-h" className="label">
            Status
          </h2>
          <span className="label figures">opened {formatDay(job.openedOn)}</span>
        </div>
        <fieldset className="segmented spaced">
          <legend className="sr-only">Job status</legend>
          {(Object.keys(JOB_STATUS_LABEL) as JobStatus[]).map((s) => (
            <label key={s}>
              <input type="radio" name="job-status" checked={job.status === s} disabled={act.pending} onChange={() => setStatus(s)} />
              <span>{JOB_STATUS_LABEL[s]}</span>
            </label>
          ))}
        </fieldset>
        <ErrorNotice error={act.error} />
      </section>

      {/* Re-mount when the plan changes so the columns match the saved stages. */}
      <PipelineBoard key={`${boardKey}/${stages.map((s) => `${s.id}:${s.name}`).join("|")}`} job={job} onCount={onCount} />

      <JobShortlists job={job} changed={pipelineChanged} />

      <JobSourcing job={job} changed={pipelineChanged} onAdded={() => setBoardKey((k) => k + 1)} />

      <section className="section" aria-labelledby="details-h">
        <div className="section-head">
          <h2 id="details-h" className="label">
            Details
          </h2>
          {!editing && (
            <button type="button" className="link-btn" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>
        {editing ? (
          <JobForm
            companyId={job.company.id}
            job={job}
            submitLabel="Save"
            onCancel={() => setEditing(false)}
            onSubmit={async (input) => {
              setJob((await api.updateJob(job.id, input)).job);
              setEditing(false);
            }}
          />
        ) : (
          <dl className="ledger">
            <div>
              <dt className="label">Fee</dt>
              <dd className="figure-xl">{formatFee(job.fee, job.currency)}</dd>
              <dd className="secondary">{job.feeFromTerms ? "from the agreed terms" : "set for this job"}</dd>
            </div>
            <div>
              <dt className="label">Owner</dt>
              <dd>{job.owner.name}</dd>
              <dt className="label spaced-top">Hiring contact</dt>
              <dd>{job.hiringContact?.fullName ?? "—"}</dd>
            </div>
            {job.skills.length > 0 && (
              <div className="wide">
                <dt className="label">Skills</dt>
                <dd className="chips small">
                  {job.skills.map((s) => (
                    <span key={s} className="chip">
                      {displaySkill(s)}
                    </span>
                  ))}
                </dd>
              </div>
            )}
            {job.description && (
              <div className="wide">
                <dt className="label">Job description</dt>
                <dd>
                  <details className="earlier">
                    <summary className="label">Show the description</summary>
                    <p className="prewrap">{job.description}</p>
                  </details>
                </dd>
              </div>
            )}
            {job.notes && (
              <div className="wide">
                <dt className="label">Notes</dt>
                <dd className="prewrap">{job.notes}</dd>
              </div>
            )}
          </dl>
        )}
      </section>

      <JobRoleTerms job={job} onSaved={setJob} />

      <StagePlan jobId={job.id} stages={stages} onSaved={setStages} />

      <section className="section">
        {confirmDelete ? (
          <p className="button-row">
            <span className="label alert">Delete this job and its interview plan?</span>
            <button
              type="button"
              className="link-btn danger"
              disabled={act.pending}
              onClick={() =>
                void act.run(async () => {
                  await api.deleteJob(job.id);
                  navigate("/jobs", { replace: true });
                })
              }
            >
              Delete permanently
            </button>
            <button type="button" className="link-btn" onClick={() => setConfirmDelete(false)}>
              Keep
            </button>
          </p>
        ) : cardCount ? (
          <p className="hint">This job has candidates on it, so it can't be deleted. Mark it lost instead.</p>
        ) : (
          <button type="button" className="link-btn danger" disabled={cardCount === null} onClick={() => setConfirmDelete(true)}>
            Delete job
          </button>
        )}
      </section>
    </>
  );
}

type Draft = { key: string; id?: string; name: string; notes: string };

/** Ordered interview stages. Existing stages keep their ids when renamed or moved. */
function StagePlan({ jobId, stages, onSaved }: { jobId: string; stages: Stage[]; onSaved: (s: Stage[]) => void }) {
  const toDrafts = (s: Stage[]): Draft[] => s.map((x) => ({ key: x.id, id: x.id, name: x.name, notes: x.notes }));
  const [drafts, setDrafts] = useState<Draft[]>(toDrafts(stages));
  const { pending, error, run } = useAction();
  useEffect(() => setDrafts(toDrafts(stages)), [stages]);

  const dirty = JSON.stringify(drafts.map(({ id, name, notes }) => ({ id, name, notes }))) !==
    JSON.stringify(stages.map(({ id, name, notes }) => ({ id, name, notes })));

  const move = (i: number, by: -1 | 1) =>
    setDrafts((d) => {
      const next = [...d];
      const [item] = next.splice(i, 1);
      next.splice(i + by, 0, item!);
      return next;
    });
  const edit = (i: number, patch: Partial<Draft>) => setDrafts((d) => d.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const save = () =>
    void run(async () => {
      const res = await api.saveStages(
        jobId,
        drafts.map((d) => ({ id: d.id, name: d.name.trim(), notes: d.notes.trim() })),
      );
      onSaved(res.stages);
    });

  return (
    <section className="section" aria-labelledby="plan-h">
      <div className="section-head">
        <h2 id="plan-h" className="label">
          Interview plan
        </h2>
        <span className="label figures">{pad(drafts.length)} stages</span>
      </div>
      <ol className="index stages">
        {drafts.map((d, i) => (
          <li key={d.key}>
            <span className="num" aria-hidden>
              {pad(i + 1)}
            </span>
            <span className="main stage-fields">
              <input
                aria-label={`Stage ${i + 1} name`}
                className="stage-name"
                value={d.name}
                maxLength={100}
                onChange={(e) => edit(i, { name: e.target.value })}
              />
              <input
                aria-label={`Stage ${i + 1} notes`}
                className="stage-notes"
                placeholder="Notes (who, format, length)"
                value={d.notes}
                maxLength={2000}
                onChange={(e) => edit(i, { notes: e.target.value })}
              />
            </span>
            <span className="actions">
              <button type="button" className="link-btn" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${d.name} up`}>
                ↑
              </button>
              <button
                type="button"
                className="link-btn"
                disabled={i === drafts.length - 1}
                onClick={() => move(i, 1)}
                aria-label={`Move ${d.name} down`}
              >
                ↓
              </button>
              <button
                type="button"
                className="link-btn danger"
                disabled={drafts.length === 1}
                onClick={() => setDrafts((x) => x.filter((_, j) => j !== i))}
                aria-label={`Remove ${d.name}`}
              >
                Remove
              </button>
            </span>
          </li>
        ))}
      </ol>
      <ErrorNotice error={error} />
      <div className="button-row spaced-top">
        <button
          type="button"
          className="link-btn"
          disabled={drafts.length >= 15}
          onClick={() => setDrafts((d) => [...d, { key: crypto.randomUUID(), name: "New stage", notes: "" }])}
        >
          + Add stage
        </button>
        {dirty && (
          <>
            <button type="button" className="btn" disabled={pending || drafts.some((d) => !d.name.trim())} onClick={save}>
              {pending ? "Saving…" : "Save plan"}
            </button>
            <button type="button" className="link-btn" onClick={() => setDrafts(toDrafts(stages))}>
              Discard changes
            </button>
          </>
        )}
      </div>
    </section>
  );
}
